import { ZuiBaseElement } from '@zywave/zui-base';
import { findAssignedElement } from '@zywave/zui-base/dist/utils/find-assigned-element.js';
import { html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { style } from './zui-formfield-css.js';
import type { PropertyValues } from 'lit';
import '@zywave/zui-tooltip';

/**
 * `<zui-formfield>` provides a standardized way of labeling and styling form controls.
 *
 * @slot - Default, unnamed slot; for inserting form controls, such as `<select>`, `<input>`, `<zui-input>`, `<zui-select>`, etc., into `<zui-formfield>`
 *
 * @csspart container - The container for form fields inserted into `zui-formfield`; this is exposed as a CSS shadow part and can be accessed with `::part(container)`.
 * @csspart label - The label for `zui-formfield`; this is exposed as a CSS shadow part and can be accessed with `::part(label)`.
 */
export class ZuiFormField extends ZuiBaseElement {
  /**
   * (optional): Label text, for the form control. Alternatively can slot in label text instead. If necessary, can be styled with `::part(label)`.
   */
  @property({ type: String })
  label: string | null = null;

  /**
   * (optional): Provide a valid CSS selector to help `zui-formfield` find the correct form control. Defaults to the first child element.
   */
  @property({ type: String, attribute: 'control-selector' })
  controlSelector = '*';

  /**
   * (optional): Tooltip text to display in a help icon next to the label.
   */
  @property({ type: String })
  tooltip: string | null = null;

  @query('slot')
  _slotEl: HTMLSlotElement;

  #validationMessageField?: string | null | undefined;

  #validationIntervalId?: number | undefined;

  #isRequired = false;

  get #validationMessage() {
    return this.#validationMessageField;
  }

  set #validationMessage(msg: string | null | undefined) {
    const oldValue = this.#validationMessageField;
    if (oldValue !== msg) {
      this.#validationMessageField = msg?.trim();
      this.requestUpdate();
    }
  }

  get #control() {
    return findAssignedElement(this._slotEl, this.controlSelector);
  }

  #checkRequired() {
    const control = this.#control;
    const wasRequired = this.#isRequired;
    this.#isRequired = control?.hasAttribute('required') ?? false;

    if (wasRequired !== this.#isRequired) {
      this.requestUpdate();
    }
  }

  static get styles() {
    return [super.styles, style];
  }

  async firstUpdated(changedProps: PropertyValues) {
    super.firstUpdated(changedProps);

    if (this.#control) {
      this.#checkRequired();
      this.#control.tagName.includes('-')
        ? customElements
            .whenDefined(this.#control.tagName.toLowerCase())
            .then(() => this.#configureValidation(this.#control as ValidatableLitElement))
        : this.#configureValidation(this.#control as ValidatableLitElement);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#validationIntervalId) {
      clearInterval(this.#validationIntervalId);
      this.#validationIntervalId = undefined;
    }
  }

  render() {
    return html`
      <div class="container" part="container">
        ${this.label
          ? html`
              <label @click="${this.#onLabelClick}" part="label" class="${this.#isRequired ? 'required' : ''}">
                <span class="label-text">${this.label}</span>
                ${this.tooltip ? html`<zui-tooltip position="right"> ${this.tooltip} </zui-tooltip>` : nothing}
              </label>
            `
          : nothing}
        <slot @slotchange="${this.#onSlotChange}"></slot>
        <div class="validation-message">${this.#validationMessage ?? ''}</div>
      </div>
    `;
  }

  #onLabelClick() {
    this.#control?.focus();
    this.#control?.click();
  }

  #onSlotChange() {
    if (this.#control) {
      this.#checkRequired();
      this.#configureValidation(this.#control as ValidatableLitElement);
    }
  }

  #configureValidation(control: ValidatableLitElement) {
    if (!control || typeof control.checkValidity !== 'function') {
      return;
    }

    control.addEventListener(
      'invalid',
      async (event) => {
        event.preventDefault();
        await this.#extractValidationMessage(control);
      },
      { capture: true }
    );

    control.addEventListener('blur', async () => await this.#extractValidationMessage(control));
    control.addEventListener('input', async () => await this.#extractValidationMessage(control));
    control.addEventListener('change', async () => await this.#extractValidationMessage(control));
    control.addEventListener('validitystatechange', async () => await this.#extractValidationMessage(control));

    // workaround native elements not necessarily raising events for direct manipulation (i.e. input.value = 'foo' doesn't trigger events)
    // see https://github.com/whatwg/html/issues/9878
    if (!control.tagName.includes('-')) {
      window.clearInterval(this.#validationIntervalId);

      this.#validationIntervalId = window.setInterval(async () => {
        if (control.matches(':user-invalid,:user-valid')) {
          await this.#extractValidationMessage(control);
        }
      }, 100);
    }
  }

  async #extractValidationMessage(control: ValidatableLitElement) {
    if (control.updateComplete) {
      await control.updateComplete;
    }
    this.#validationMessage = control.validationMessage;
  }
}

window.customElements.define('zui-formfield', ZuiFormField);

declare global {
  interface HTMLElementTagNameMap {
    'zui-formfield': ZuiFormField;
  }
}

type ValidatableLitElement = ValidatableHTMLElement & {
  updateComplete?: Promise<void>;
};

type ValidatableHTMLElement = HTMLElement & {
  setCustomValidity(message: string): void;
  checkValidity(): boolean;
  reportValidity(): boolean;
  validationMessage: string;
  validity: ValidityState;
};
